import React from "react";

import {
  ClipboardList,
  DocumentText,
} from "../../../components/icons/svg/outline";
import {
  emptyRfiCardSummary,
  emptyTmCardSummary,
} from "../shared/projectTicketSummaries";

/** Soft pill backgrounds aligned with the Projects Home card mockup. */
const STATUS_PILL_TONES = {
  blue: "border-sky-200 bg-sky-50 text-sky-700",
  orange: "border-orange-200 bg-orange-50 text-orange-700",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

/**
 * One status count pill in a T&M / RFI row.
 *
 * @param {Object} props
 * @param {string} props.label - Status label.
 * @param {number} props.count - Status count.
 * @param {"blue"|"orange"|"green"} props.tone - Soft pill color tone.
 * @returns {JSX.Element}
 */
function StatusCountPill({ label, count, tone }) {
  const toneClass = STATUS_PILL_TONES[tone] || STATUS_PILL_TONES.blue;
  const isZero = !(Number(count) > 0);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none ${toneClass}`}
    >
      <span>{label}</span>
      <span className={`tabular-nums ${isZero ? "font-normal" : "font-bold"}`}>
        {isZero ? "-" : Number(count)}
      </span>
    </span>
  );
}

/**
 * Compact T&M / RFI summary row with a leading icon.
 *
 * By default only statuses with count &gt; 0 are shown. When ``showZeroMetrics``
 * is on, all pills render (including zeros) and the row always appears.
 *
 * @param {Object} props
 * @param {string} props.title - Module title (e.g. ``T&M``, ``RFI``).
 * @param {React.ComponentType<{className?: string}>} props.Icon - Leading icon.
 * @param {Array<{ label: string, count: number, tone: "blue"|"orange"|"green" }>} props.pills - Status counts.
 * @param {boolean} [props.isLoading=false] - Whether ticket data is still loading.
 * @param {boolean} [props.showZeroMetrics=false] - Keep zero-count pills visible.
 * @param {string} [props.iconClassName="text-gray-400"] - Icon color classes.
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element|null}
 */
export const CompactTicketSummary = ({
  title,
  Icon,
  pills,
  isLoading = false,
  showZeroMetrics = false,
  iconClassName = "text-gray-400",
  className = "",
}) => {
  const visibleRows = showZeroMetrics
    ? pills || []
    : (pills || []).filter((pill) => Number(pill.count) > 0);
  if (!isLoading && visibleRows.length === 0) {
    return null;
  }

  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      <Icon
        className={`h-4 w-4 flex-shrink-0 ${iconClassName}`}
        aria-hidden
      />
      <span className="flex-shrink-0 text-xs font-semibold text-gray-700">
        {title}
      </span>
      {isLoading ? (
        <span className="text-xs text-gray-400">Loading…</span>
      ) : (
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-1.5">
          {visibleRows.map((pill) => (
            <StatusCountPill
              key={pill.label}
              label={pill.label}
              count={Number(pill.count) || 0}
              tone={pill.tone}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * T&M row: To send / To sign / To price / To submit (pipeline stages still open).
 *
 * @param {Object} props
 * @param {Object} [props.summary] - Compact T&M status summary.
 * @param {boolean} [props.isLoading=false] - Whether ticket lists are still loading.
 * @param {boolean} [props.showZeroMetrics=false] - Keep zero-count pills visible.
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element|null}
 */
export function ProjectTmSummaryTile({
  summary = emptyTmCardSummary(),
  isLoading = false,
  showZeroMetrics = false,
  className = "",
}) {
  return (
    <CompactTicketSummary
      title="T&M"
      Icon={ClipboardList}
      iconClassName="text-green-700"
      pills={[
        { label: "To Send", count: summary.toSend, tone: "blue" },
        { label: "To Sign", count: summary.toSign, tone: "orange" },
        { label: "To Price", count: summary.toPrice, tone: "green" },
        { label: "To Submit", count: summary.toSubmit, tone: "green" },
      ]}
      isLoading={isLoading}
      showZeroMetrics={showZeroMetrics}
      className={className}
    />
  );
}

/**
 * RFI row: To send (open + reviewing), To close (awaiting response).
 *
 * @param {Object} props
 * @param {Object} [props.summary] - Compact RFI status summary.
 * @param {boolean} [props.isLoading=false] - Whether ticket lists are still loading.
 * @param {boolean} [props.showZeroMetrics=false] - Keep zero-count pills visible.
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element|null}
 */
export function ProjectRfiSummaryTile({
  summary = emptyRfiCardSummary(),
  isLoading = false,
  showZeroMetrics = false,
  className = "",
}) {
  return (
    <CompactTicketSummary
      title="RFI"
      Icon={DocumentText}
      iconClassName="text-blue-600"
      pills={[
        { label: "To Send", count: summary.toSend, tone: "blue" },
        { label: "To Close", count: summary.toClose, tone: "orange" },
      ]}
      isLoading={isLoading}
      showZeroMetrics={showZeroMetrics}
      className={className}
    />
  );
}

export default CompactTicketSummary;
